import Avatar from "@material-ui/core/Avatar";
import Button from "@material-ui/core/Button";
import Card from "@material-ui/core/Card";
import CardContent from "@material-ui/core/CardContent";
import Chip from "@material-ui/core/Chip";
import type { Theme } from "@material-ui/core/styles";
import { makeStyles } from "@material-ui/core/styles";
import MuiTable from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import Typography from "@material-ui/core/Typography";
import { format } from "date-fns";
import ja from "date-fns/locale/ja";
import { Link as RouterLink } from "react-router-dom";
import ProviderIcon from "../../../components/ProviderIcon";
import { getURL } from "../../../models/media";
import { UserListItem } from "../../../models/user";

const useStyles = makeStyles((theme: Theme) => ({
  content: {
    overflow: "auto",
    padding: 0,
  },
  inner: {
    minWidth: 1380,
  },
  table: {
    tableLayout: "fixed",
    width: "100%",
  },
  wideCell: {
    width: "34%",
  },
  nameCell: {
    width: "23%",
  },
  statusCell: {
    width: "13%",
  },
  timeCell: {
    width: "15%",
  },
  actionCell: {
    width: 96,
  },
  userCell: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1.5),
    minWidth: 0,
  },
  avatar: {
    width: 40,
    height: 40,
    flexShrink: 0,
  },
  userText: {
    minWidth: 0,
  },
  emailRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-start",
    gap: theme.spacing(0.5),
    minWidth: 0,
  },
  uidText: {
    whiteSpace: "nowrap",
  },
  emailText: {
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    minWidth: 0,
  },
  providerIcon: {
    display: "inline-flex",
    alignItems: "center",
    flexShrink: 0,
    color: theme.palette.text.secondary,
    lineHeight: 0,
    "& svg": {
      fontSize: 14,
    },
  },
  statusChips: {
    display: "flex",
    flexDirection: "column" as const,
    alignItems: "center",
    gap: theme.spacing(0.5),
  },
  chipActive: {
    backgroundColor: "#4caf50",
    color: "#fff",
    fontWeight: 600,
  },
}));

function formatName(user: UserListItem): string {
  const name = [user.lastName, user.firstName].filter(Boolean).join(" ");
  return name || "-";
}

function formatGender(gender?: string): string {
  if (gender === "male") {
    return "男";
  }
  if (gender === "female") {
    return "女";
  }
  return "";
}

function formatAge(birthYear?: number): string {
  if (!birthYear) {
    return "";
  }
  const age = new Date().getFullYear() - birthYear;
  if (age <= 0) {
    return "";
  }
  return `${age}歳`;
}

function formatProfileMeta(user: UserListItem): string {
  const prefecture = user.prefecture || "";
  const age = formatAge(user.birthYear);
  const gender = formatGender(user.gender);
  const ageAndGender =
    age && gender ? `${age}(${gender})` : age || (gender ? `(${gender})` : "");

  return [prefecture, ageAndGender].filter(Boolean).join("・") || "-";
}

export default function Table({
  className,
  users,
}: {
  className?: string;
  users: UserListItem[];
}) {
  const classes = useStyles();

  return (
    <Card className={className}>
      <CardContent className={classes.content}>
        <div className={classes.inner}>
          <MuiTable className={classes.table}>
        <TableHead>
          <TableRow>
            <TableCell className={classes.wideCell}>
              UID
              <br />
              メールアドレス
            </TableCell>
            <TableCell className={classes.nameCell}>
              氏名
              <br />
              (ニックネーム)
            </TableCell>
            <TableCell align="center" className={classes.statusCell}>
              購入状況
            </TableCell>
                <TableCell className={classes.timeCell}>
                  アカウント作成日時
                </TableCell>
                <TableCell className={classes.timeCell}>
                  最終ログイン日時
                </TableCell>
                <TableCell className={classes.actionCell} />
              </TableRow>
            </TableHead>
          <TableBody>
            {users.map((user) => (
              <TableRow hover={true} key={user.uid}>
                <TableCell className={classes.wideCell}>
                  <div className={classes.userCell}>
                    <Avatar
                      className={classes.avatar}
                      src={user.avatar ? getURL(user.avatar) : undefined}
                    />
                    <div className={classes.userText}>
                      <Typography className={classes.uidText} noWrap>
                        {user.uid}
                      </Typography>
                      <div className={classes.emailRow}>
                        <Typography className={classes.emailText} noWrap>
                          {user.email || "-"}
                        </Typography>
                        <span className={classes.providerIcon}>
                          {Array.from(new Set(user.providers)).map((p) => (
                            <ProviderIcon key={p} provider={p} />
                          ))}
                        </span>
                      </div>
                    </div>
                  </div>
                </TableCell>
                <TableCell className={classes.nameCell}>
                  <Typography noWrap>{formatName(user)}</Typography>
                  <Typography color="textSecondary" noWrap>
                    ({user.nickname || "-"})
                  </Typography>
                  <Typography color="textSecondary" noWrap>
                    {formatProfileMeta(user)}
                  </Typography>
                </TableCell>
                  <TableCell align="center" className={classes.statusCell}>
                    <div className={classes.statusChips}>
                      {user.hasSubscription && (
                        <Chip
                          label="サブスク"
                          size="small"
                          className={classes.chipActive}
                        />
                      )}
                      {user.hasOneTimePurchase && (
                        <Chip
                          label="買い切り"
                          size="small"
                          className={classes.chipActive}
                        />
                      )}
                      {!user.hasSubscription && !user.hasOneTimePurchase && (
                        <Typography variant="body2" color="textSecondary">
                          -
                        </Typography>
                      )}
                    </div>
                  </TableCell>
                  <TableCell className={classes.timeCell}>
                    {format(
                      user.userMetadata.creationTime,
                      "yyyy-MM-dd HH:mm:ss",
                      {
                        locale: ja,
                      }
                    )}
                  </TableCell>
                  <TableCell className={classes.timeCell}>
                    {format(
                      user.userMetadata.lastLogInTime,
                      "yyyy-MM-dd HH:mm:ss",
                      {
                        locale: ja,
                      }
                    )}
                  </TableCell>
                  <TableCell align="right" className={classes.actionCell}>
                    <Button
                      component={RouterLink}
                      to={`/users/${user.uid}`}
                      color="primary"
                      size="small"
                      variant="contained"
                    >
                      詳細
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </MuiTable>
        </div>
      </CardContent>
    </Card>
  );
}
